/**
 * The admin as the API serves it and the browser sends it. Every route under
 * `/admin` checks a permission; these are the shapes of what passes.
 */
import { z } from "zod";
import { Email, UserId } from "./auth.js";
import { FieldChecks } from "./field-checks.js";
import { BrandId, CategoryId, ImageId, Slug, StoredInteger, StoredText } from "./catalog.js";
import { OptionalText, ReviewStatus } from "./reviews.js";
import { ShippingMethodCode } from "./checkout.js";
import { CalendarDay, OrderSort, OrderStatus } from "./orders.js";
import { StaffRole } from "./staff.js";
export const Page = {
    /** The page and page size every admin list takes. */
    params: {
        page: z.coerce.number().int().min(1).max(10_000).default(1),
        perPage: z.coerce.number().int().min(1).max(100).default(20),
    },
    /** How many pages `total` items fill, never fewer than one. */
    count: (total, perPage) => Math.max(1, Math.ceil(total / perPage)),
};
export const RoleChange = {
    schema: z.strictObject({
        roles: z
            .array(StaffRole.schema)
            .max(StaffRole.NAMES.length)
            .transform((roles) => StaffRole.NAMES.filter((name) => roles.includes(name))),
    }),
};
export const AdminUserQuery = {
    schema: z.object({
        /** Only accounts that hold this staff role. */
        role: StaffRole.schema.optional(),
        /** Only accounts that hold any staff role. */
        staff: z.stringbool().optional(),
        /** The one account with exactly this address. */
        email: Email.schema.optional(),
        ...Page.params,
    }),
};
export const AuditQuery = {
    schema: z
        .object({
        actor: UserId.schema.optional(),
        /** An exact action, or the start of one ending in a dot, such as `product.`. */
        action: z
            .string()
            .trim()
            .min(1)
            .max(60)
            .regex(/^[a-z_.]+$/, "Not an action name.")
            .optional(),
        targetType: z
            .string()
            .trim()
            .min(1)
            .max(40)
            .regex(/^[a-z_]+$/, "Not a target type.")
            .optional(),
        targetId: z.string().trim().min(1).max(80).refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE).optional(),
        from: CalendarDay.schema.optional(),
        to: CalendarDay.schema.optional(),
        ...Page.params,
    })
        .refine((query) => query.from === undefined || query.to === undefined || query.from <= query.to, {
        message: "The start date must not be after the end date.",
        path: ["to"],
    }),
};
export const ShippingMethodInput = {
    schema: z
        .strictObject({
        // Staff type the code in, where checkout's message would ask them to choose one.
        code: z.string({ error: "Enter a code." }).trim().min(1, "Enter a code.").pipe(ShippingMethodCode.schema),
        name: z.string().trim().min(1, "Enter a name.").max(60, "Use at most 60 characters."),
        priceCents: z
            .number({ error: "Enter a price." })
            .int("Enter whole cents.")
            .min(0, "The price cannot be negative.")
            .max(100_000, "Use at most €1,000.00."),
        /** Left out when the option never ships free. */
        freeFromCents: z
            .number()
            .int("Enter whole cents.")
            .min(1, "Enter a threshold above zero, or none.")
            .max(10_000_000, "Use at most €100,000.00.")
            .optional(),
        minDays: z.number().int("Enter whole days.").min(1, "Use 1 to 30 days.").max(30, "Use 1 to 30 days."),
        maxDays: z.number().int("Enter whole days.").min(1, "Use 1 to 30 days.").max(30, "Use 1 to 30 days."),
        isActive: z.boolean(),
        position: z.number().int().min(0).max(1000),
    })
        .refine((input) => input.minDays <= input.maxDays, {
        message: "The latest day cannot come before the earliest.",
        path: ["maxDays"],
        when: FieldChecks.runWhenValid("minDays", "maxDays"),
    }),
};
/** A carrier's tracking number, as staff type it. */
export const TrackingNumber = {
    schema: z
        .string({ error: "Enter the tracking number." })
        .trim()
        .min(4, "Enter the tracking number.")
        .max(40, "Use at most 40 characters.")
        .regex(/^[A-Za-z0-9-]+$/, "Use letters, digits and dashes."),
};
export const OrderStatusChange = {
    schema: z.discriminatedUnion("status", [
        z.strictObject({ status: z.literal("processing") }),
        z.strictObject({ status: z.literal("shipped"), trackingNumber: TrackingNumber.schema }),
        z.strictObject({ status: z.literal("delivered") }),
    ]),
};
export const AdminOrderQuery = {
    schema: z
        .object({
        status: z
            .union([OrderStatus.schema, z.array(OrderStatus.schema).max(OrderStatus.NAMES.length)])
            .transform((value) => (typeof value === "string" ? [value] : value))
            .optional(),
        from: CalendarDay.schema.optional(),
        to: CalendarDay.schema.optional(),
        /** The order with exactly this number. */
        number: z.coerce.number().int().positive().max(StoredInteger.MAX).optional(),
        /** The orders whose contact email is exactly this. */
        email: Email.schema.optional(),
        sort: OrderSort.schema.default("newest"),
        ...Page.params,
    })
        .refine((query) => query.from === undefined || query.to === undefined || query.from <= query.to, {
        message: "The start date must not be after the end date.",
        path: ["to"],
    }),
};
/** What a picture shows, for people who cannot see it. Required of every product picture. */
export const ImageAlt = {
    schema: z
        .string({ error: "Describe what the picture shows." })
        .trim()
        .min(1, "Describe what the picture shows.")
        .max(200, "Use at most 200 characters."),
};
/** The pictures of one product. */
export const ProductPictures = {
    /** The most pictures one product shows. */
    MAX: 12,
};
export const ImageOrderInput = {
    schema: z.strictObject({
        images: z
            .array(z.strictObject({ id: ImageId.schema, alt: ImageAlt.schema }))
            .min(1)
            .max(ProductPictures.MAX)
            .refine((images) => new Set(images.map((image) => image.id)).size === images.length, "Each picture appears once."),
    }),
};
export const AdminReviewQuery = {
    schema: z.object({
        status: ReviewStatus.schema.default("pending"),
        ...Page.params,
    }),
};
/** The rules every product field follows, in the admin's form and in an import alike. */
export const ProductFields = {
    name: z
        .string({ error: "Enter a name." })
        .trim()
        .min(1, "Enter a name.")
        .max(200, "Use at most 200 characters.")
        .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
    description: z
        .string({ error: "Enter a description." })
        .trim()
        .min(1, "Enter a description.")
        .max(5000, "Use at most 5,000 characters.")
        .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
    priceCents: z
        .number({ error: "Enter a price." })
        .int("Enter whole cents.")
        .min(1, "The price must be at least €0.01.")
        .max(10_000_000, "Use at most €100,000.00."),
    stock: z
        .number({ error: "Enter the stock." })
        .int("Enter a whole number.")
        .min(0, "The stock cannot be negative.")
        .max(1_000_000, "Use at most 1,000,000."),
    weightGrams: z
        .number({ error: "Enter the weight." })
        .int("Enter whole grams.")
        .min(1, "Enter at least 1 gram.")
        .max(1_000_000, "Use at most 1,000,000 grams."),
    millimeters: z
        .number({ error: "Enter the size." })
        .int("Enter whole millimeters.")
        .min(1, "Enter at least 1 mm.")
        .max(10_000, "Use at most 10,000 mm."),
    /** A slug a person types: required, then held to the slug rules. */
    slug: z.string({ error: "Enter a slug." }).trim().min(1, "Enter a slug.").pipe(Slug.schema),
    /** A category or brand named by its slug in an import. */
    reference: z.string({ error: "Name it by its slug." }).trim().min(1, "Name it by its slug.").pipe(Slug.schema),
    propertyName: z
        .string()
        .trim()
        .min(1, "Name the property.")
        .max(40, "Use at most 40 characters.")
        .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
    propertyValue: z
        .string()
        .trim()
        .min(1, "Enter a value.")
        .max(200, "Use at most 200 characters.")
        .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
    /** The most named properties one product carries. */
    MAX_PROPERTIES: 30,
};
/** A product's named facts, with names unique however they are capitalized. */
export const ProductProperties = {
    schema: z
        .array(z.strictObject({ name: ProductFields.propertyName, value: ProductFields.propertyValue }))
        .max(ProductFields.MAX_PROPERTIES, "Use at most 30 properties.")
        .refine((properties) => new Set(properties.map((property) => property.name.toLowerCase())).size === properties.length, "Each property name appears once."),
};
export const ProductInput = {
    schema: z.strictObject({
        name: ProductFields.name,
        slug: ProductFields.slug,
        description: ProductFields.description,
        priceCents: ProductFields.priceCents,
        stock: ProductFields.stock,
        categoryId: CategoryId.schema,
        brandId: BrandId.schema,
        weightGrams: ProductFields.weightGrams,
        widthMm: ProductFields.millimeters,
        heightMm: ProductFields.millimeters,
        depthMm: ProductFields.millimeters,
        properties: ProductProperties.schema,
        /** Whether the home page shows it among the featured products. */
        isFeatured: z.boolean(),
    }),
};
export const ProductUpdate = {
    schema: ProductInput.schema.extend({ stockBefore: ProductFields.stock }),
};
export const AdminProductSort = {
    NAMES: ["name_asc", "name_desc", "newest", "oldest", "stock_asc", "stock_desc", "price_asc", "price_desc"],
    schema: z.enum(["name_asc", "name_desc", "newest", "oldest", "stock_asc", "stock_desc", "price_asc", "price_desc"]),
    label: {
        name_asc: "Name, A to Z",
        name_desc: "Name, Z to A",
        newest: "Newest",
        oldest: "Oldest",
        stock_asc: "Stock, lowest first",
        stock_desc: "Stock, highest first",
        price_asc: "Price, lowest first",
        price_desc: "Price, highest first",
    },
    /** A sort named by its field alone, as a bookmark may carry it, and the order it stands for. */
    byField: new Map([
        ["name", "name_asc"],
        ["stock", "stock_asc"],
        ["price", "price_asc"],
    ]),
};
export const AdminProductQuery = {
    schema: z.object({
        /** Part of the name or the slug. */
        q: z.string().trim().max(120).refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE).optional(),
        category: Slug.schema.optional(),
        brand: Slug.schema.optional(),
        featured: z.stringbool().optional(),
        /** Only products with `LOW_STOCK` or fewer in stock. */
        lowStock: z.stringbool().optional(),
        sort: z.preprocess((value) => AdminProductSort.byField.get(value) ?? value, AdminProductSort.schema).default("name_asc"),
        ...Page.params,
    }),
    /** The most a product holds and still counts as low on stock, in the list's filter and the dashboard's count. */
    LOW_STOCK: 5,
};
export const CategoryInput = {
    schema: z.strictObject({
        slug: ProductFields.slug,
        name: z
            .string({ error: "Enter a name." })
            .trim()
            .min(1, "Enter a name.")
            .max(80, "Use at most 80 characters.")
            .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
        /** What the category page says about it; empty is none. */
        description: OptionalText.of(1000, "Use at most 1,000 characters."),
    }),
};
export const BrandInput = {
    schema: z.strictObject({
        slug: ProductFields.slug,
        name: z
            .string({ error: "Enter a name." })
            .trim()
            .min(1, "Enter a name.")
            .max(80, "Use at most 80 characters.")
            .refine(StoredText.hasNoNul, StoredText.NUL_MESSAGE),
    }),
};
export const ImportRow = {
    schema: z.strictObject({
        slug: ProductFields.slug,
        name: ProductFields.name,
        description: ProductFields.description,
        priceCents: ProductFields.priceCents,
        stock: ProductFields.stock,
        category: ProductFields.reference,
        brand: ProductFields.reference,
        weightGrams: ProductFields.weightGrams,
        widthMm: ProductFields.millimeters,
        heightMm: ProductFields.millimeters,
        depthMm: ProductFields.millimeters,
        featured: z.boolean().optional(),
        properties: ProductProperties.schema.default([]),
    }),
};
//# sourceMappingURL=admin.js.map